import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import type { UseFormTrigger } from 'react-hook-form';
import { client } from '../../services/api';
import { createPatientInKinnserKinnserPatientsPost } from '../../services/api/sdk.gen';
import type { KinnserCreatePatientResponse } from '../../services/api/types.gen';
import type { CreatePatientFormData } from '../../schemas/createPatientFormSchema';

interface UseKinnserCreationOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  trigger: UseFormTrigger<CreatePatientFormData>;
  /** When true, skips react-hook-form validation before calling the API. Useful for specialized flows like ROC scheduling. */
  skipValidation?: boolean;
}

interface UseKinnserCreationReturn {
  creatingInKinnser: boolean;
  createResult: KinnserCreatePatientResponse | null;
  handleCreateInKinnser: () => Promise<void>;
}

/**
 * Hook for creating patients in Kinnser.
 * Manages the creation state and handles the API call.
 */
export function useKinnserCreation({
  intakeId,
  intakeStatus,
  trigger,
  skipValidation = false,
}: UseKinnserCreationOptions): UseKinnserCreationReturn {
  const [creatingInKinnser, setCreatingInKinnser] = useState(false);
  const [createResult, setCreateResult] =
    useState<KinnserCreatePatientResponse | null>(null);

  const clearKinnserCreateResult = useCallback(() => {
    setCreateResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearKinnserCreateResult
  );

  const handleCreateInKinnser = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API unless explicitly skipped
    if (!skipValidation) {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }
    }

    setCreatingInKinnser(true);
    setCreateResult(null);

    try {
      const response = await createPatientInKinnserKinnserPatientsPost({
        client,
        body: { intake_id: intakeId },
      });

      if (response.data) {
        setCreateResult(response.data);
      }
    } catch (error) {
      console.error('Error creating patient in Kinnser:', error);
      setCreateResult({
        success: false,
        kinnser_patient_id: null,
        errors: [
          {
            field: 'general',
            code: 'UNEXPECTED_ERROR',
            message: 'An unexpected error occurred. Please try again.',
          },
        ],
      });
    } finally {
      setCreatingInKinnser(false);
    }
  }, [intakeId, trigger, skipValidation]);

  return {
    creatingInKinnser,
    createResult,
    handleCreateInKinnser,
  };
}
